import React from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { PS } from '../theme';
import { Icon } from '../lib/icons';
import Screen from '../components/Screen';
import { getArticle } from '../data/knowledge';

const CALLOUT = {
  terra: { bg: 'oklch(0.62 0.12 38 / 0.1)', fg: 'oklch(0.4 0.12 38)', bar: 'oklch(0.62 0.12 38)' },
  green: { bg: 'oklch(0.78 0.06 155 / 0.22)', fg: 'oklch(0.32 0.08 155)', bar: 'oklch(0.48 0.08 155)' },
  warn: { bg: 'oklch(0.88 0.08 80 / 0.4)', fg: 'oklch(0.38 0.1 60)', bar: 'oklch(0.62 0.1 80)' },
  danger: { bg: 'oklch(0.62 0.12 28 / 0.1)', fg: 'oklch(0.46 0.14 28)', bar: 'oklch(0.52 0.14 28)' },
  ink: { bg: 'rgba(32,28,24,0.05)', fg: PS.text, bar: PS.ink },
};

function Block({ b }) {
  if (b.type === 'p') return <p style={{ fontSize: 14, lineHeight: 1.7, color: PS.text, margin: '0 0 14px' }}>{b.text}</p>;
  if (b.type === 'h') return <div style={{ fontSize: 15.5, fontWeight: 700, letterSpacing: -0.2, margin: '6px 0 8px' }}>{b.text}</div>;
  if (b.type === 'callout') {
    const t = CALLOUT[b.tone] || CALLOUT.ink;
    return <div style={{ background: t.bg, borderLeft: `3px solid ${t.bar}`, borderRadius: 12, padding: '12px 14px', margin: '4px 0 16px', fontSize: 13, lineHeight: 1.6, color: t.fg, fontWeight: 500 }}>{b.text}</div>;
  }
  if (b.type === 'list') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 10, margin: '0 0 16px' }}>
      {b.items.map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
          <div style={{ width: 6, height: 6, borderRadius: '50%', background: PS.terracotta, marginTop: 8, flexShrink: 0 }} />
          <div style={{ fontSize: 13.5, lineHeight: 1.6 }}>{it}</div>
        </div>
      ))}
    </div>
  );
  if (b.type === 'check') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 8, margin: '0 0 16px' }}>
      {b.items.map((it, i) => (
        <div key={i} style={{ display: 'flex', gap: 10, alignItems: 'flex-start', background: PS.surface, border: `1px solid ${PS.line}`, borderRadius: 12, padding: '10px 12px' }}>
          <div style={{ width: 20, height: 20, borderRadius: 6, background: 'oklch(0.78 0.06 155 / 0.3)', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0, marginTop: 1 }}><Icon.check size={13} stroke="oklch(0.38 0.08 155)" sw={2.5} /></div>
          <div style={{ fontSize: 13, lineHeight: 1.55 }}>{it}</div>
        </div>
      ))}
    </div>
  );
  if (b.type === 'steps') return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, margin: '0 0 16px' }}>
      {b.items.map((s, i) => (
        <div key={i} style={{ display: 'flex', gap: 12 }}>
          <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center' }}>
            <div className="num" style={{ width: 26, height: 26, borderRadius: 13, background: PS.ink, color: PS.page, display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: 13, flexShrink: 0 }}>{i + 1}</div>
            {i < b.items.length - 1 && <div style={{ flex: 1, width: 1, background: PS.line, marginTop: 2 }} />}
          </div>
          <div style={{ paddingBottom: 16 }}>
            <div style={{ fontSize: 13.5, fontWeight: 600 }}>{s.title}</div>
            <div style={{ fontSize: 12.5, color: PS.textMuted, marginTop: 2, lineHeight: 1.55 }}>{s.text}</div>
          </div>
        </div>
      ))}
    </div>
  );
  if (b.type === 'table') return (
    <div style={{ border: `1px solid ${PS.line}`, borderRadius: 14, overflow: 'hidden', margin: '0 0 16px' }}>
      {b.rows.map((r, i) => (
        <div key={i} style={{ display: 'grid', gridTemplateColumns: '1fr 1.4fr', borderBottom: i < b.rows.length - 1 ? `1px solid ${PS.lineSoft}` : 'none' }}>
          <div style={{ padding: '10px 12px', fontSize: 12.5, fontWeight: 600, background: PS.surface, borderRight: `1px solid ${PS.lineSoft}` }}>{r[0]}</div>
          <div style={{ padding: '10px 12px', fontSize: 12, color: PS.textMuted, lineHeight: 1.5 }}>{r[1]}</div>
        </div>
      ))}
    </div>
  );
  if (b.type === 'example') return (
    <div style={{ background: PS.ink, color: PS.page, borderRadius: 14, padding: 16, margin: '0 0 16px' }}>
      <div style={{ fontSize: 11, color: 'oklch(0.82 0.1 82)', letterSpacing: 0.8, textTransform: 'uppercase', fontWeight: 600, marginBottom: 10 }}>ตัวอย่าง · {b.title}</div>
      <div style={{ display: 'flex', flexDirection: 'column', gap: 7 }}>
        {b.lines.map((l, i) => (
          <div key={i} style={{ display: 'flex', gap: 8, alignItems: 'flex-start' }}>
            <div style={{ width: 5, height: 5, borderRadius: '50%', background: 'oklch(0.72 0.12 82)', marginTop: 7, flexShrink: 0 }} />
            <div className="num" style={{ fontSize: 13, lineHeight: 1.55, color: '#f5f1ea' }}>{l}</div>
          </div>
        ))}
      </div>
    </div>
  );
  return null;
}

export default function Article() {
  const { id } = useParams();
  const nav = useNavigate();
  const a = getArticle(id);
  if (!a) return <Screen><div style={{ padding: 40, color: PS.textMuted }}>ไม่พบบทความ</div></Screen>;
  const IcoC = Icon[a.icon] || Icon.info;
  return (
    <Screen bg={PS.page}>
      <div style={{ padding: '14px 16px 12px', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, borderBottom: `1px solid ${PS.line}`, background: PS.surface2 }}>
        <button data-testid="article-back-btn" onClick={() => nav(-1)} className="ps-press" style={{ border: 'none', background: 'transparent', padding: 0 }}><Icon.chevronL size={20} stroke={PS.ink} /></button>
        <div style={{ fontSize: 11, color: PS.textMuted, letterSpacing: 0.8, textTransform: 'uppercase' }}>คู่มือเล่นแชร์</div>
      </div>
      <div style={{ flex: 1, overflow: 'auto', padding: '20px 20px 40px' }}>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', marginBottom: 14 }}>
          <div style={{ width: 40, height: 40, borderRadius: 12, background: PS.ink, display: 'flex', alignItems: 'center', justifyContent: 'center' }}><IcoC size={20} stroke={PS.page} /></div>
          <div>
            <div style={{ fontSize: 11, color: PS.terracotta, fontWeight: 600, letterSpacing: 0.3 }}>{a.category} · อ่าน {a.read} นาที</div>
          </div>
        </div>
        <h1 style={{ fontSize: 24, fontWeight: 700, letterSpacing: -0.5, lineHeight: 1.2, margin: '0 0 6px' }}>{a.title}</h1>
        <div style={{ fontSize: 13.5, color: PS.textMuted, marginBottom: 22 }}>{a.subtitle}</div>
        {a.blocks.map((b, i) => <Block key={i} b={b} />)}
        <div style={{ marginTop: 8, padding: 12, background: 'rgba(32,28,24,0.03)', borderRadius: 12, fontSize: 10.5, color: PS.textMuted, lineHeight: 1.5 }}>
          ข้อมูลทั่วไปเพื่อความเข้าใจ ไม่ใช่คำแนะนำทางกฎหมายหรือการลงทุนเฉพาะราย — การเล่นแชร์มีความเสี่ยงผิดนัดชำระ ควรพิจารณาตามกำลังและปรึกษาผู้เชี่ยวชาญเมื่อจำเป็น
        </div>
      </div>
    </Screen>
  );
}
